// eslint-disable @typescript-eslint/no-unsafe-call
import { AxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useGetModelUsers } from '../../../../api/masters/masters';
import { ErrorInfo, ProductionSearchConditions } from '../../../../api/model';
import {
  useGetProductionMasters,
  usePostProductionsSearch,
  usePostProductionsSearchDownload,
} from '../../../../api/productions/productions';
import { useDefaultOptions, useOptionsForBinary } from '../../../../hooks/api';
import { executeDownload } from '../../../../utils/download';
import * as sessionStorageUtils from '../../../../utils/sessionStorage';
import ErrorComponent from '../../../atoms/ErrorComponent';
import NoDataComponent from '../../../atoms/NoDataComponent';
import WaitComponent from '../../../atoms/WaitComponent';
import BlobErrorModal from '../../../modules/BlobErrorModal';
import SpinnerModal from '../../../modules/SpinnerModal';
import ProductionsList from './ProductionsList';
import ProductionsSearchConditions from './ProductionsSearchConditions';

/**
 * タブタイトル
 * @returns
 */
function Title() {
  // 言語情報
  const { t } = useTranslation();
  return (
    <Helmet>
      <title>{t('CMS-POC|制作一覧')}</title>
    </Helmet>
  );
}

function IndexPage() {
  // 検索条件部分が開いているかどうか
  const [openSearchConditions, setOpenSearchConditions] = useState(true);

  // 言語情報
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // マスタ、組織情報の取得
  const options = useDefaultOptions(language);
  const {
    data: mastersData,
    isLoading: mastersIsLoading,
    isError: mastersIsError,
    error: mastersError,
  } = useGetProductionMasters(options);
  const {
    data: organizationsData,
    isLoading: organizationsIsLoading,
    isError: organizationsIsError,
    error: organizationsError,
  } = useGetModelUsers(options);

  // 初期検索条件（セッションストレージに保存されている）
  const [defaultConditions] = useState<ProductionSearchConditions>(
    sessionStorageUtils.get<ProductionSearchConditions>('Productions.SearchConditions', {})
  );

  // 検索条件
  const refConditions = useRef(sessionStorageUtils.get<ProductionSearchConditions>('Productions.SearchConditions', {}));

  // 検索 API 情報
  const {
    data,
    isLoading,
    isError,
    error,
    mutateAsync: searchAsync,
  } = usePostProductionsSearch<AxiosError<ErrorInfo>, unknown>(options);

  // 一覧の初回表示
  useEffect(() => {
    const postProductions = async () => {
      await searchAsync({ data: defaultConditions });
    };
    void postProductions();
  }, [defaultConditions, searchAsync]);

  // 一覧ダウンロード API
  const optionsForBinary = useOptionsForBinary(language);
  const {
    mutateAsync: downloadAsync,
    error: downloadError,
    isError: isDownloadError,
    isLoading: downloading,
  } = usePostProductionsSearchDownload<AxiosError<Blob>>(optionsForBinary);

  // ダウンロードエラーの表示
  const [displayDownloadError, setDisplayDownloadError] = useState(false);
  useEffect(() => {
    setDisplayDownloadError(isDownloadError);
  }, [isDownloadError]);

  // 一覧出力ボタン押下時
  const onClickDownload = useCallback(async () => {
    // ダウンロード API へのアクセス
    await downloadAsync(
      { data: refConditions.current },
      {
        // API アクセスが成功したら、ダウンロードを実行
        onSuccess: (response) => executeDownload(response),
      }
    );
  }, [downloadAsync]);

  // 条件を保存し、検索する
  const setConditionsAndSearch = useCallback(
    (next: ProductionSearchConditions) => {
      const postProductions = async () => {
        // API アクセスが成功した場合
        const onSuccess = () => {
          sessionStorageUtils.set('Productions.SearchConditions', next);
          refConditions.current = next;
        };

        // API アクセスの実行
        await searchAsync({ data: next }, { onSuccess });
      };
      void postProductions();
    },
    [searchAsync]
  );

  // 待ち状態
  if (mastersIsLoading || organizationsIsLoading) {
    return (
      <div id="main">
        <Title />
        <WaitComponent />
      </div>
    );
  }

  // エラー
  if (mastersIsError || organizationsIsError) {
    return (
      <div id="main">
        <Title />
        {mastersIsError && <ErrorComponent error={mastersError} />}
        {organizationsIsError && <ErrorComponent error={organizationsError} />}
      </div>
    );
  }

  // データが取得できない場合
  if (
    mastersData.data.destinations == null ||
    mastersData.data.doors == null ||
    mastersData.data.engines == null ||
    mastersData.data.factories == null ||
    mastersData.data.production_divisions == null ||
    mastersData.data.companies == null ||
    mastersData.data.development_scales == null ||
    mastersData.data.model_names == null ||
    organizationsData.data.companies == null
  ) {
    return (
      <div id="main">
        <Title />
        <NoDataComponent />
      </div>
    );
  }

  return (
    <div id="main">
      <Title />
      <section>
        <h3 className="trig">
          <span>
            {t('検索条件')}
            {/* 検索条件の開閉 */}
            <button
              type="button"
              className="text-button"
              onClick={(e) => {
                e.preventDefault();
                setOpenSearchConditions((pre) => !pre);
              }}
            >
              <i className={`fa-solid ${openSearchConditions ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
            </button>
          </span>
        </h3>
        {/* 検索条件 */}
        <ProductionsSearchConditions
          openSearchConditions={openSearchConditions}
          defaultConditions={defaultConditions}
          conditionsRef={refConditions}
          setConditionsAndSearch={setConditionsAndSearch}
          destinations={mastersData.data.destinations}
          modelNames={mastersData.data.model_names}
          doors={mastersData.data.doors}
          engines={mastersData.data.engines}
          factories={mastersData.data.factories}
          treeCompanies={organizationsData.data.companies}
          companies={mastersData.data.companies}
          productionDevisions={mastersData.data.production_divisions}
          developmentScales={mastersData.data.development_scales}
        />
        {/* 制作一覧の操作 */}
        <div className="ttlsep prolist">
          <div className="left">
            <h3 className="trig">
              <span>{t('制作一覧')}</span>
            </h3>
          </div>
          <div className="right">
            <ul>
              <li>
                <Link to="./add">
                  <i className="fa-solid fa-circle-plus" />
                  {t('新規資料追加')}
                </Link>
              </li>
              <li>
                <button type="button" className="text-button" onClick={onClickDownload}>
                  <i className="fa-solid fa-download" />
                  {t('一覧出力')}
                </button>
              </li>
              {/*
              <li>
                {data === undefined ? 0 : data.data.productions?.length}件
              </li>
               */}
            </ul>
          </div>
        </div>
        {/* 制作一覧 */}
        <ProductionsList data={data} isLoading={isLoading} isError={isError} error={error} />
      </section>
      {/* スピナーモーダルの表示 */}
      {downloading && <SpinnerModal />}
      {/* ダウンロードエラーの表示 */}
      {displayDownloadError && (
        <BlobErrorModal error={downloadError} onClickOk={() => setDisplayDownloadError(false)} />
      )}
    </div>
  );
}

export default IndexPage;
